import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Sparkles, Gift, BookOpen, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import PageSubHeader from '@/components/PageSubHeader';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';

/**
 * Landing page for `/plans/join/:code`.
 * - Fetches a public preview of the shared custom plan.
 * - If the visitor isn't signed in, punts them to `/login?returnTo=…`
 *   so they come back here after auth.
 * - On Accept, calls the join endpoint which clones the plan into the
 *   recipient's account, auto-enrolls them, and navigates them
 *   straight to Day 1.
 */
export default function JoinCustomPlanPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | notfound | accepting

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/reading-plans/custom/join/${code}`);
        if (cancelled) return;
        setPreview(res.data);
        setState('ready');
      } catch {
        if (cancelled) return;
        setState('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const handleAccept = async () => {
    setState('accepting');
    try {
      const res = await axios.post(
        `${API}/reading-plans/custom/join/${code}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.cloned ? 'Plan added to your library!' : 'Reopening your copy…');
      navigate(`/study-plans/${res.data.plan_id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Send them to sign in and bounce back here after.
        navigate(`/login?returnTo=${encodeURIComponent(`/plans/join/${code}`)}`);
        return;
      }
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || 'Could not accept the plan';
      toast.error(message);
      setState('ready');
    }
  };

  if (state === 'loading') {
    return (
      <div className="page-container" style={{ background: 'var(--theme-background)' }}>
        <Header />
        <div className="page-content flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
        </div>
      </div>
    );
  }

  if (state === 'notfound') {
    return (
      <div className="page-container" style={{ background: 'var(--theme-background)' }}>
        <Header />
        <PageSubHeader onBack={() => navigate('/study-plans')} title="Shared plan" backTestId="shared-plan-back" />
        <div className="page-content max-w-md mx-auto px-6 pt-10 text-center">
          <Gift className="h-10 w-10 mx-auto mb-4" style={{ color: 'var(--theme-textSecondary)' }} />
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
            This share link is not available
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--theme-textSecondary)' }}>
            The plan may have been deleted or the link mistyped. Ask your friend to send it again.
          </p>
          <Button onClick={() => navigate('/study-plans')} data-testid="join-plan-browse-btn">
            Browse plans
          </Button>
        </div>
      </div>
    );
  }

  const plan = preview?.plan || {};
  const sharedBy = preview?.shared_by || 'a friend';
  const cover = plan.cover_color || '#6366f1';

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }}>
      <Header />
      <PageSubHeader onBack={() => navigate('/study-plans')} title="Shared plan" backTestId="shared-plan-back" />
      <div className="page-content max-w-lg mx-auto px-5 pt-6 pb-24">
        <div
          className="rounded-3xl p-6 mb-5"
          style={{
            background: `linear-gradient(135deg, ${cover}20 0%, ${cover}05 100%)`,
            border: `1px solid ${cover}44`,
          }}
          data-testid="shared-plan-preview"
        >
          <div className="flex items-center gap-2 mb-3">
            <Gift className="h-4 w-4" style={{ color: cover }} />
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: cover }}>
              {sharedBy} shared this with you
            </p>
          </div>
          <h1 className="text-2xl font-bold leading-tight mb-2" style={{ color: 'var(--theme-text)' }}>
            {plan.title || 'Custom study plan'}
          </h1>
          {plan.short_description && (
            <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
              {plan.short_description}
            </p>
          )}
          <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            <span className="inline-flex items-center gap-1">
              <BookOpen className="h-3.5 w-3.5" /> {plan.length_days} days
            </span>
            {plan.build_cadence && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> {plan.build_cadence.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>

        {plan.description && (
          <p className="text-sm leading-relaxed mb-6 whitespace-pre-line" style={{ color: 'var(--theme-text)', opacity: 0.9 }}>
            {plan.description}
          </p>
        )}

        <Button
          onClick={handleAccept}
          disabled={state === 'accepting'}
          className="w-full rounded-2xl py-6 gap-2 text-base font-semibold"
          style={{ background: cover, color: '#fff' }}
          data-testid="accept-shared-plan-btn"
        >
          {state === 'accepting'
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Adding to your library…</>
            : <><Sparkles className="h-4 w-4" /> Add this plan to my library</>}
        </Button>
        <p className="text-[11px] text-center mt-3" style={{ color: 'var(--theme-textSecondary)' }}>
          You'll get your own independent copy — complete it at your pace and adjust it however you like.
        </p>
      </div>
    </div>
  );
}
